import { z } from "zod";

import { getApplicableReasons, opnameBuckets } from "./stock-opname-utils";

import type { StockOpnameItem, StockReason } from "@artisancode/api-types";

/** Per-line problems shown next to the field, keyed by opname line id. */
export type OpnameItemErrors = Record<
  string,
  { onHand?: string; repacking?: string; reason?: string }
>;

// Mirrors the API's completion rules so the counter sees every problem at once,
// instead of the server rejecting one item at a time.
export function completeStockOpnameSchema(
  isRawMaterial: boolean,
  reasons: StockReason[],
) {
  return z
    .array(z.custom<StockOpnameItem>())
    .min(1, "Tambahkan minimal satu barang sebelum menyelesaikan opname.")
    .superRefine((items, ctx) => {
      items.forEach((item, index) => {
        const issue = (
          field: keyof OpnameItemErrors[string],
          message: string,
        ) => ctx.addIssue({ code: "custom", path: [index, field], message });

        // Same rule as isOpnameLineCounted, split per field so each input gets its own message.
        const { onHand: hasOnHand, repacking: hasRepacking } = opnameBuckets(
          isRawMaterial,
          item.kind,
        );

        if (hasOnHand && item.countedOnHand === null) {
          issue("onHand", "Belum dihitung");
        }
        if (hasRepacking && item.countedRepacking === null) {
          issue("repacking", "Belum dihitung");
        }

        const diffOnHand = hasOnHand ? item.diffOnHand : 0;
        const diffRepacking = hasRepacking ? item.diffRepacking : 0;
        if (diffOnHand === 0 && diffRepacking === 0) return;
        const applicable = getApplicableReasons(
          { diffOnHand, diffRepacking },
          reasons,
        );
        if (!applicable.some((r) => r.id === item.reasonId)) {
          issue("reason", "Pilih alasan selisih");
        }
      });
    });
}

/** Runs the schema and returns field errors per line plus one summary line for the toast. */
export function validateStockOpnameCompletion(
  items: StockOpnameItem[],
  isRawMaterial: boolean,
  reasons: StockReason[],
): { errors: OpnameItemErrors; summary: string | null } {
  const result = completeStockOpnameSchema(isRawMaterial, reasons).safeParse(
    items,
  );
  if (result.success) return { errors: {}, summary: null };

  const errors: OpnameItemErrors = {};
  for (const issue of result.error.issues) {
    const [index, field] = issue.path as [
      number,
      keyof OpnameItemErrors[string],
    ];
    const item = items[index];
    if (!item) return { errors: {}, summary: issue.message }; // empty session
    (errors[item.id] ??= {})[field] = issue.message;
  }

  const lines = Object.values(errors);
  const uncounted = lines.filter((e) => e.onHand || e.repacking).length;
  const missingReason = lines.filter((e) => e.reason).length;
  const parts = [
    uncounted > 0 && `${uncounted} barang belum dihitung`,
    missingReason > 0 && `${missingReason} barang belum diberi alasan selisih`,
  ].filter(Boolean);
  return {
    errors,
    summary: `Opname belum bisa diselesaikan: ${parts.join(", ")}.`,
  };
}
